// A/B assignment for the upgrade / $1-trial modal COPY rewrite (QA copy test).
//
//   control — today's copy (the baseline).
//   variant — the rewritten copy: the price + trial terms stated once directly
//             under the headline, the same Pro feature set on BOTH modals
//             (led by "Unlimited projects", which Free caps at 3), and a
//             soft-exit ("Not now") beside the pay CTA.
//
// Assignment is a stable hash of the Supabase user id — mirroring the Finish
// Line (lib/credits/finish-line-experiment.ts) and Building Streak
// (lib/streak/experiment.ts) experiments — so a user lands in the same arm on
// every surface, session and device with no assignment table. It is resolved
// SERVER-SIDE (the trial-eligibility + rated-status endpoints) and handed to the
// client, so the bucketing logic and the kill switch never ship to the browser.
//
// The hash is namespaced with its own `upgrade_copy:` prefix so this split is
// INDEPENDENT of the other experiments' buckets — a user in finish_line is not
// thereby pushed into this variant, which would confound both readouts.
//
// Rollout is controlled by the UPGRADE_COPY_ROLLOUT env var so the test can be
// flipped without a deploy:
//   "off" | "control" → everyone on control (rewrite dark).
//   "on"  | "all"     → everyone on the rewrite (ship to 100%).
//   "experiment"      → an even 50/50 hash split (the A/B test).
// Outside production the default is "on" so the rewrite is visible locally and
// on preview branches without setting anything; in production the default is
// the 50/50 split the test calls for.

export type UpgradeCopyVariant = "control" | "variant";

/** FNV-1a → an even bucket in [0, 100). Deterministic, no Math.random. */
function bucketPercent(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 100;
}

export function resolveUpgradeCopyVariant(
  userId: string | null | undefined,
): UpgradeCopyVariant {
  const rollout = (process.env.UPGRADE_COPY_ROLLOUT ?? "").trim().toLowerCase();

  // Explicit overrides win and don't need a user id.
  if (rollout === "off" || rollout === "control") return "control";
  if (rollout === "on" || rollout === "all") return "variant";

  // Bucketing needs a stable id; without one, fall back to the safe baseline.
  // Signed-out surfaces (the pre-auth /start exit-intent) therefore always see
  // control rather than a per-render coin flip that no analysis could join.
  if (!userId) return "control";

  if (rollout === "experiment") {
    return bucketPercent(`upgrade_copy:${userId}`) < 50 ? "variant" : "control";
  }

  // Unset: rewrite on off-production, real 50/50 split in production.
  if (process.env.NODE_ENV !== "production") return "variant";
  return bucketPercent(`upgrade_copy:${userId}`) < 50 ? "variant" : "control";
}

/** Narrowing guard for values arriving from the wire / props. */
export function isUpgradeCopyVariant(
  v: string | null | undefined,
): v is UpgradeCopyVariant {
  return v === "control" || v === "variant";
}

/** True when the surface should render the rewritten copy (arm B). */
export function showsUpgradeCopyVariant(
  v: UpgradeCopyVariant | null | undefined,
): boolean {
  return v === "variant";
}

/**
 * Coerce anything off the wire into a usable arm. Unknown/missing → "control",
 * so a stale client or a failed fetch degrades to the baseline copy rather than
 * silently entering the test arm.
 */
export function coerceUpgradeCopyVariant(
  v: string | null | undefined,
): UpgradeCopyVariant {
  return isUpgradeCopyVariant(v) ? v : "control";
}
